import {
  Card,
  Profile,
  ReviewPlan,
  isReadyToLearn,
  isRetired,
  newWordsLearnedSince,
  planReview,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { CloudDeckWord, CloudDecksApi, DeckLists, getCloudDecksApi } from './cloudDecks.js';
import { classifyDeck, loadLearnerState } from './deckStatus.js';
import { newWordsPerDay } from './newWordsSettings.js';
import { isPendingCard } from './pendingCardStore.js';

/**
 * What a review contains (Docs/plans/CLOUD_DECKS_PLAN.md): due cards, then the next words from "Up next
 * to learn", then - when that list has nothing ready - questions about the next undecided words of
 * the deck the learner follows. The rules live in `planReview` (packages/core); this reads the
 * learner's data for it and finds the deck questions.
 */

/** The learner's local midnight: "today" for the new-words limit. */
export function startOfLocalDay(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export interface ReviewSnapshot {
  profile: Profile;
  cards: Card[];
  plan: ReviewPlan;
  /** The daily limit for new words. */
  perDay: number;
  /** Words learned for the first time since local midnight. */
  learnedToday: number;
  /** Waiting cards that could be introduced (text prepared), whatever the limit says. */
  readyWaiting: number;
  /** Chosen words whose card text is still being prepared. */
  preparing: number;
}

export async function loadReviewSnapshot(now: Date = new Date()): Promise<ReviewSnapshot> {
  const [profile, cards, logs] = await Promise.all([
    StorageManager.getProfile(),
    StorageManager.getCards(),
    StorageManager.getReviewLogs(),
  ]);
  const perDay = newWordsPerDay(profile);
  const learnedToday = newWordsLearnedSince(logs, startOfLocalDay(now));
  const plan = planReview({
    cards,
    sessionSize: profile.cards_per_session || 5,
    newWordsPerDay: perDay,
    newWordsLearnedToday: learnedToday,
    now,
  });
  return {
    profile,
    cards,
    plan,
    perDay,
    learnedToday,
    readyWaiting: cards.filter(isReadyToLearn).length,
    preparing: cards.filter((c) => !isRetired(c) && c.fsrs_state === 'New' && isPendingCard(c))
      .length,
  };
}

export interface FollowedDeck {
  id: string;
  title: string;
  /** Every word of the deck, in deck order. */
  words: CloudDeckWord[];
  /** How many words the deck has on the server: more than `words.length` when they could not be read. */
  wordCount: number;
}

/** Whether the saved lists name a followed deck that is among the saved decks. */
function followedKnown(lists: DeckLists): boolean {
  return !!lists.followingId && lists.decks.some((d) => d.id === lists.followingId);
}

/**
 * The deck the learner follows, with its words from the saved copy (a deck never opened on this
 * device is read from the server once, unless `fetchMissing` is false). When the saved lists name
 * no followed deck, `refreshLists` asks the server first: the deck may have been followed on
 * another device or on the website, and this device's lists are only refreshed on the Decks tab.
 * Null when no deck is followed or it is not available.
 */
export async function loadFollowedDeck(
  options: { api?: CloudDecksApi; fetchMissing?: boolean; refreshLists?: boolean } = {}
): Promise<FollowedDeck | null> {
  const api = options.api ?? (await getCloudDecksApi());
  let lists = await api.readLists();
  if (options.refreshLists && !followedKnown(lists)) lists = (await api.refreshLists()).lists;
  if (!lists.followingId) return null;
  let open = await api.readDeck(lists.followingId);
  if (!open) return null;
  if (open.words.length === 0 && open.deck.word_count > 0 && options.fetchMissing !== false) {
    open = (await api.refreshDeck(lists.followingId, { force: true })) ?? open;
  }
  return {
    id: open.deck.id,
    title: open.deck.title,
    words: open.words,
    wordCount: open.deck.word_count,
  };
}

export interface DeckQuestion {
  word: CloudDeckWord;
  /** The word's place in the deck (1-based) and the deck's size: "word 90 of 214". */
  position: number;
  total: number;
}

/**
 * The next word to ask about: the first undecided word of the deck in the order the deck page
 * shows (deck order, "Not now" words after the rest), skipping words already asked in this
 * review so a "Not now" word does not come straight back. Null when nothing is left to ask.
 */
export async function nextDeckQuestion(
  deck: FollowedDeck,
  asked: ReadonlySet<string> = new Set(),
  api?: CloudDecksApi
): Promise<DeckQuestion | null> {
  const service = api ?? (await getCloudDecksApi());
  const [learner, lists] = await Promise.all([loadLearnerState(), service.readLists()]);
  const { undecided, words } = classifyDeck(deck.words, learner, lists.deferrals);
  const word = undecided.find((w) => !asked.has(w.id)) as CloudDeckWord | undefined;
  if (!word) return null;
  return {
    word,
    position: words.findIndex((c) => c.word.id === word.id) + 1,
    total: words.length,
  };
}

/**
 * True when opening a review now would show something: a due card, a new word from Up next, or a
 * question about the followed deck. Reads only what is saved on this device.
 */
export async function reviewHasWork(now: Date = new Date()): Promise<boolean> {
  const { plan } = await loadReviewSnapshot(now);
  if (plan.due.length > 0 || plan.fresh.length > 0) return true;
  if (plan.deckQuestions <= 0) return false;
  try {
    const deck = await loadFollowedDeck({ fetchMissing: false });
    return !!deck && (await nextDeckQuestion(deck)) !== null;
  } catch {
    return false;
  }
}

/**
 * True when there is nothing left for the learner right now: no card is due, no word waits in
 * "Up next" (ready or still being prepared) and no followed deck has a word left to ask about.
 * Only then is it worth suggesting a deck or new words; while reviews are still due (more than one
 * review's worth), the next reminder brings them.
 *
 * "Nothing followed" and "the deck has no words" are only believed after asking the server when
 * this device has no followed deck or no words for it. When the deck cannot be read (offline), the
 * answer is false: better to say nothing than to offer a deck the learner already follows.
 */
export async function isOutOfWords(now: Date = new Date(), api?: CloudDecksApi): Promise<boolean> {
  const cards = await StorageManager.getCards();
  if (cards.some((c) => !isRetired(c) && c.fsrs_state === 'New')) return false;
  const nowIso = now.toISOString();
  if (cards.some((c) => !isRetired(c) && c.fsrs_state !== 'New' && c.due_date <= nowIso)) {
    return false;
  }
  try {
    const service = api ?? (await getCloudDecksApi());
    if (!followedKnown(await service.readLists())) {
      // Nothing followed on this device: ask the server before believing it.
      if (!(await service.refreshLists()).online) return false;
    }
    const deck = await loadFollowedDeck({ api: service });
    if (deck) {
      if (deck.words.length === 0 && deck.wordCount > 0) return false; // could not read its words
      if (await nextDeckQuestion(deck, new Set(), service)) return false;
    }
  } catch {
    return false; // not sure: no need to suggest anything
  }
  return true;
}
